import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useVoiceAgent } from '../hooks/useVoiceAgent';
import { agentBus, executeDirective, useAgentHandlers } from './agentBus';
import SITE from '../site';

const API = process.env.REACT_APP_UPLOAD_API || '';

const ROUTE_MAP = {
  home: '/',
  movies: '/movies',
  photos: '/photos',
  forecast: '/forecast',
  swell_map: '/swell-map',
  globe: '/globe',
  trips: '/trips',
  docs: '/docs',
  jobs: '/jobs',
  costs: '/costs',
};

function pageNameFromPath(path) {
  if (path.startsWith('/movies/') && /\/(studio|color|edit|social)/.test(path)) return 'Movie editor';
  if (path.startsWith('/photo/')) return 'Photo editor';
  if (path.startsWith('/clip/')) return 'Clip color editor';
  if (path === '/movies') return 'Movies';
  if (path === '/photos') return 'Photos';
  if (path === '/forecast') return 'Forecast';
  if (path === '/swell-map') return 'Swell map';
  if (path === '/docs') return 'Docs';
  return SITE.siteName;
}

// Saved chat-panel position (drag persists across sessions).
const POS_KEY = 'cs-agent-pos';
const loadPos = () => {
  try {
    const p = JSON.parse(localStorage.getItem(POS_KEY) || 'null');
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null;
  } catch { return null; }
};
const clampPos = (p, w, h) => ({
  x: Math.max(4, Math.min(p.x, window.innerWidth - w - 4)),
  y: Math.max(4, Math.min(p.y, window.innerHeight - Math.min(h, 120))),
});

// Global voice copilot. Mounted once in App (inside the navbar's user area);
// talks to the relay and dispatches the agent's UI directives through
// agentBus. Navigation is registered here so the agent can move between pages
// even when no editor is open. The collapsed trigger is a small navbar emoji;
// the expanded chat panel is a portal you can DRAG anywhere by its header.
export default function AgentDock() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [messages, setMessages] = useState([]); // finished turns (voice + text)
  const [chatBusy, setChatBusy] = useState(false);
  const [pos, setPos] = useState(loadPos); // null = CSS default (top right)
  const panelRef = useRef(null);
  const dragRef = useRef(null); // { dx, dy } pointer offset inside the panel
  const convoRef = useRef(null);

  const pushMsg = useCallback((role, content) => {
    setMessages((prev) => [...prev.slice(-30), { id: `${role}-${Date.now()}-${Math.random()}`, role, content }]);
  }, []);

  // App-level handlers: navigation (others are registered by the editors).
  useAgentHandlers(
    useMemo(
      () => ({
        navigate: ({ target }) => {
          const route = ROUTE_MAP[target];
          if (route) { navigate(route); return { ok: true, route }; }
          return { ok: false, error: `unknown target ${target}` };
        },
        // Emitted by the find_content server tool to open a specific record.
        open_route: ({ route }) => {
          if (typeof route === 'string' && route.startsWith('/')) { navigate(route); return { ok: true, route }; }
          return { ok: false, error: 'bad route' };
        },
      }),
      [navigate],
    ),
  );

  const getToken = useCallback(() => (user ? user.getIdToken() : Promise.resolve('')), [user]);
  const getInit = useCallback(() => ({ pageName: pageNameFromPath(location.pathname) }), [location.pathname]);

  const {
    status, error, level, userText, assistantText, connect, disconnect, sendText,
    micMuted, toggleMicMuted, speakerMuted, toggleSpeakerMuted, configured,
  } = useVoiceAgent({
    getToken,
    getInit,
    onUserFinal: useCallback((text) => pushMsg('user', text), [pushMsg]),
    onAssistantFinal: useCallback((text) => pushMsg('assistant', text), [pushMsg]),
  });

  // Keep the newest turn in view.
  useEffect(() => {
    if (convoRef.current) convoRef.current.scrollTop = convoRef.current.scrollHeight;
  }, [messages, userText, assistantText, open]);

  // Text-chat fallback: typed messages don't need a mic session — they go to
  // POST /voice/chat (same brain + tool registry); returned UI actions run
  // through the same directive dispatcher the voice path uses.
  const sendChat = useCallback(async (text) => {
    setChatBusy(true);
    try {
      const history = [...messages, { role: 'user', content: text }]
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }));
      const c = agentBus.getContext();
      const r = await fetch(`${API}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
        body: JSON.stringify({
          messages: history,
          context: { pageName: c.pageName || pageNameFromPath(location.pathname), movieName: c.movieName, clips: c.clips, presets: c.presets, gradeSummary: c.gradeSummary },
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `chat failed (${r.status})`);
      (data.actions || []).forEach((a) => executeDirective(a.name, a.arguments));
      pushMsg('assistant', data.reply || '(no reply)');
    } catch (e) {
      pushMsg('assistant', `⚠ ${e.message}`);
    } finally {
      setChatBusy(false);
    }
  }, [messages, getToken, location.pathname, pushMsg]);

  if (!user || !configured) return null;

  const live = status === 'live';
  const connecting = status === 'connecting';

  // Header mic: toggles the live session. Navbar emoji: opens the chat panel
  // (text works right away — voice starts only from the 🎙 button); closing
  // the panel leaves a live session running — the emoji shows 🔴 while live
  // so it's never silently hot.
  const toggle = () => {
    if (live || connecting) disconnect();
    else connect();
  };
  const navClick = () => setOpen((o) => !o);

  const submitText = (e) => {
    e.preventDefault();
    const text = typed.trim();
    if (!text || chatBusy) return;
    setTyped('');
    if (live) {
      pushMsg('user', text);
      sendText(text);
    } else {
      pushMsg('user', text);
      sendChat(text);
    }
  };

  const ring = 0.5 + Math.min(0.5, level);

  // Drag the expanded panel by its header. Pointer events cover mouse + touch;
  // position is viewport-fixed, clamped on move AND on resize, persisted.
  const onDragStart = (e) => {
    if (e.target.closest('button, input')) return; // header buttons still click
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    // First drag from the CSS-default spot: pin current rect as the position.
    if (!pos) setPos({ x: r.left, y: r.top });
    e.preventDefault();
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const rr = panelRef.current ? panelRef.current.getBoundingClientRect() : { width: 320, height: 200 };
      setPos(clampPos({ x: ev.clientX - d.dx, y: ev.clientY - d.dy }, rr.width, rr.height));
    };
    const onUp = () => {
      dragRef.current = null;
      setPos((p) => {
        if (p) try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* private mode */ }
        return p;
      });
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Keep a saved position on screen if the window shrinks.
  useEffect(() => {
    if (!open || !pos) return undefined;
    const onResize = () => {
      const r = panelRef.current ? panelRef.current.getBoundingClientRect() : { width: 320, height: 200 };
      setPos((p) => (p ? clampPos(p, r.width, r.height) : p));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, !!pos]);

  const panel = open ? createPortal(
    <div
      ref={panelRef}
      className="cs-agent-panel"
      style={pos ? { left: pos.x, top: pos.y, right: 'auto' } : undefined}
    >
      <div className="cs-agent-head" onPointerDown={onDragStart} title="Drag to move">
        <span className="cs-agent-grip">⋮⋮</span>
        <span className={`cs-agent-dot ${live ? 'live' : status}`} />
        <strong>Copilot</strong>
        <span className="cs-agent-status">
          {live ? (micMuted ? 'Mic muted' : 'Listening…') : connecting ? 'Connecting…' : status === 'error' ? 'Error' : 'Text chat'}
        </span>
        {live && (
          <button
            className={`cs-agent-micsm ${micMuted ? 'off' : ''}`}
            onClick={toggleMicMuted}
            title={micMuted ? 'Unmute mic' : 'Mute mic'}
          >
            {micMuted ? '🚫' : '🎤'}
          </button>
        )}
        {live && (
          <button
            className={`cs-agent-micsm ${speakerMuted ? 'off' : ''}`}
            onClick={toggleSpeakerMuted}
            title={speakerMuted ? 'Unmute speaker' : 'Mute speaker'}
          >
            {speakerMuted ? '🔇' : '🔊'}
          </button>
        )}
        <button
          className={`cs-agent-micsm ${live ? 'live' : ''}`}
          onClick={toggle}
          title={live || connecting ? 'Stop voice session' : 'Start voice session'}
        >
          {live || connecting ? '■' : '🎙'}
        </button>
        <button className="cs-agent-x" onClick={() => setOpen(false)} title="Hide">×</button>
      </div>
      {error && <div className="cs-agent-err">{error}</div>}
      <div className="cs-agent-convo" ref={convoRef}>
        {messages.map((m) => (
          <div key={m.id} className={`cs-agent-line ${m.role === 'user' ? 'you' : 'bot'}`}>
            <span>{m.role === 'user' ? 'You' : 'Grok'}</span>{m.content}
          </div>
        ))}
        {userText && <div className="cs-agent-line you live"><span>You</span>{userText}</div>}
        {assistantText && <div className="cs-agent-line bot live"><span>Grok</span>{assistantText}</div>}
        {chatBusy && <div className="cs-agent-line bot live"><span>Grok</span>…</div>}
        {!messages.length && !userText && !assistantText && (
          <div className="cs-agent-hint">
            {live
              ? 'Try “make it warmer”, “give me 3 looks”, “darken the sky”, or “open the color stage”.'
              : 'Type below — no mic needed. Tap 🎙 for a live voice session.'}
          </div>
        )}
      </div>
      <form className="cs-agent-form" onSubmit={submitText}>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={live ? 'Type a command…' : 'Ask or command — text only…'} disabled={chatBusy} />
        <button type="submit" disabled={chatBusy}>Send</button>
      </form>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        className={`cs-agent-navbtn ${live ? 'live' : ''} ${connecting ? 'connecting' : ''}`}
        style={live ? { boxShadow: `0 0 0 ${Math.round(ring * 6)}px rgba(56,189,248,0.18)` } : undefined}
        onClick={navClick}
        title={live ? 'Voice copilot — live (click to open chat)' : 'Voice copilot'}
        aria-label="Voice copilot"
      >
        {live ? '🔴' : '🎙'}
      </button>
      {panel}
    </>
  );
}

export { agentBus };
